import './App.css';

import {useEffect, useRef, useState} from "react";

import { Client } from "@langchain/langgraph-sdk";
import {useViewportHeight} from "./hook/useViewportHeight";

const toPlainText = (content) => {
    if (typeof content === "string") {
        return content;
    }
    if (content && typeof content === "object" && !Array.isArray(content)) {
        if ((content.type === "text" || typeof content.type === "undefined") && typeof content.text === "string") {
            return content.text;
        }
    }
    if (Array.isArray(content)) {
        return content
            .map((part) => {
                if (typeof part === "string") {
                    return part;
                }
                if (part && typeof part === "object") {
                    if (part.type === "text" || typeof part.type === "undefined") {
                        return typeof part.text === "string" ? part.text : "";
                    }
                }
                return "";
            })
            .join("");
    }
    return "";
};

const normalizeEventName = (eventName) => {
    if (typeof eventName !== "string") {
        return "";
    }
    return eventName.split("|")[0];
};

const normalizeMessageType = (rawType) => {
    if (typeof rawType !== "string") {
        return "";
    }
    if (rawType.endsWith("MessageChunk")) {
        return rawType.slice(0, -"MessageChunk".length).toLowerCase();
    }
    if (rawType.endsWith("Message")) {
        return rawType.slice(0, -"Message".length).toLowerCase();
    }
    return rawType.toLowerCase();
};

const getMessageType = (message) => normalizeMessageType(
    message?.type
    ?? message?.role
    ?? message?.data?.type
    ?? message?.data?.role
    ?? message?.kwargs?.type
    ?? message?.kwargs?.role
);

const getMessageId = (message, fallbackId) => (
    message?.id
    ?? message?.data?.id
    ?? message?.kwargs?.id
    ?? fallbackId
);

const getMessageContent = (message) => (
    message?.content
    ?? message?.data?.content
    ?? message?.data?.kwargs?.content
    ?? message?.kwargs?.content
    ?? ""
);

const isAssistantMessage = (message) => {
    const messageType = getMessageType(message);
    if (!messageType) {
        return true;
    }
    return messageType === "ai"
        || messageType === "assistant"
        || messageType === "ai_message"
        || messageType === "assistant_message";
};

const CHAT_SAVE_ENDPOINT = process.env.REACT_APP_CHAT_SAVE_ENDPOINT || "http://95.163.231.119/api/chat/messages";

const toNullableInt = (value) => {
    if (value === null || typeof value === "undefined") {
        return null;
    }
    const numberValue = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numberValue)) {
        return null;
    }
    return Math.trunc(numberValue);
};

const getClientIdentity = () => {
    if (typeof window === "undefined") {
        return { userId: 0, chatId: 0 };
    }

    const readInt = (key) => {
        const rawValue = window.localStorage.getItem(key);
        const parsed = Number(rawValue);
        return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
    };

    let userId = readInt("chat_user_id");
    if (!userId) {
        userId = Math.trunc(Date.now() / 1000);
        window.localStorage.setItem("chat_user_id", String(userId));
    }

    let chatId = readInt("chat_id");
    if (!chatId) {
        chatId = userId;
        window.localStorage.setItem("chat_id", String(chatId));
    }

    return { userId, chatId };
};

const saveChatMessage = async ({ threadId, role, messageText, messageId }) => {
    if (!threadId || !messageText) {
        return;
    }

    const { userId, chatId } = getClientIdentity();
    const payload = {
        thread_id: threadId,
        telegram_user_id: userId,
        chat_id: chatId,
        role,
        message_text: messageText,
    };

    const normalizedMessageId = toNullableInt(messageId);
    if (normalizedMessageId !== null) {
        payload.message_id = normalizedMessageId;
    }

    try {
        await fetch(CHAT_SAVE_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
    } catch (error) {
        console.error("Error saving chat message:", error);
    }
};

function App() {
    useViewportHeight()

    const input = useRef(null)
    const boardRef = useRef(null)

    const [messages, setMessages] = useState([]);
    const [thread, setThread] = useState(null);
    const [disabledSend, setDisabledSend] = useState(false);
    const [draft, setDraft] = useState("");
    useEffect(() => {
        setTimeout(() => {
            initThread();
        }, 4000)
    }, []);

    // useEffect(() => {
    //     if (thread) {
    //         initChat();
    //     }
    // }, [thread])

    // only set the apiUrl if you changed the default port when calling langgraph dev
    const client = new Client({
        apiUrl: "http://95.163.231.119",
        // apiUrl: "http://localhost:8123",
        // defaultHeaders: { Authorization: "Bearer <token>" },
    });


    useEffect(() => {
        if (!boardRef.current) {
            return;
        }
        boardRef.current.scrollTop = boardRef.current.scrollHeight;
    }, [messages]);

    const initThread = async () => {
        await client.threads.create().then((thread) => {
            setThread(thread);
        });
    };

    const initChat = async () => {
        try {
            console.log(thread)
            const response = await client.runs.create(
                thread["thread_id"],
                "test_2",
                {
                    input: {
                        'messages': [
                            {
                                'role': 'system',
                                'content': '{"initPage": "' + document.querySelector('title').text + '"}'
                            }
                        ]
                    }
                });
            console.log(response.data);
            // Handle streaming response if needed
        } catch (error) {
            console.error('Error initializing chat:', error);
        }
    }

    const handleSend = async (messageText) => {
        const trimmedText = messageText.trim();
        if (!trimmedText) {
            return;
        }
        const threadId = thread?.["thread_id"];
        const userMessageId = Date.now().toString();
        const fallbackAiMessageId = `${Date.now()}-ai`;

        setMessages((prevMessages) => [
            ...prevMessages,
            {
                message: trimmedText,
                id: userMessageId,
                sender: "human",
                direction: "outgoing",
            }
        ]);
        void saveChatMessage({
            threadId,
            role: "user",
            messageText: trimmedText,
            messageId: userMessageId,
        });
        setDisabledSend(true);

        const assistantMessages = new Map();
        let assistantMessagesFlushed = false;
        const flushAssistantMessages = async () => {
            if (assistantMessagesFlushed) {
                return;
            }
            if (!threadId || assistantMessages.size === 0) {
                return;
            }
            const payloads = [];
            for (const [messageId, messageText] of assistantMessages.entries()) {
                if (!messageText || !messageText.trim()) {
                    continue;
                }
                payloads.push(saveChatMessage({
                    threadId,
                    role: "assistant",
                    messageText,
                    messageId,
                }));
            }
            await Promise.all(payloads);
            assistantMessagesFlushed = true;
        };

        try {
            const streamResponse = client.runs.stream(
                thread["thread_id"], // Threadless run
                "test_2", // Assistant ID
                {
                    input: {
                        "messages": [
                            {"role": "user", "content": trimmedText}
                        ]
                    },
                    streamMode: ["messages-tuple", "messages"],
                }
            )

            let hasTupleStream = false;

            for await (const chunk of streamResponse) {
                const eventName = normalizeEventName(chunk.event);

                if (eventName === "messages" || eventName === "messages-tuple") {
                    hasTupleStream = true;
                    console.log(chunk)
                    const message = Array.isArray(chunk.data)
                        ? chunk.data[0]
                        : (chunk.data?.message ?? chunk.data);
                    if (!message) {
                        continue;
                    }

                    if (!isAssistantMessage(message)) {
                        continue;
                    }

                    const deltaText = toPlainText(getMessageContent(message));
                    if (!deltaText) {
                        continue;
                    }

                    const messageId = getMessageId(message, fallbackAiMessageId);
                    const currentText = assistantMessages.get(messageId) || "";
                    assistantMessages.set(messageId, `${currentText}${deltaText}`);

                    setMessages((prevMessages) => {
                        const updatedMessages = [...prevMessages];
                        const existingIndex = updatedMessages.findIndex((m) => m.id === messageId);

                        if (existingIndex === -1) {
                            updatedMessages.push({
                                message: deltaText,
                                id: messageId,
                                sender: "ai",
                                direction: "incoming",
                            });
                        } else {
                            updatedMessages[existingIndex] = {
                                ...updatedMessages[existingIndex],
                                message: `${updatedMessages[existingIndex].message}${deltaText}`,
                            };
                        }

                        return updatedMessages;
                    });
                    continue;
                }

                if ((eventName === "messages/partial" || eventName === "messages/complete") && !hasTupleStream) {
                    const messagesOut = Array.isArray(chunk.data)
                        ? chunk.data
                        : (chunk.data?.messages ?? (chunk.data?.message ? [chunk.data.message] : []));

                    setMessages((prevMessages) => {
                        const updatedMessages = [...prevMessages];

                        for (const newMsg of messagesOut) {
                            if (!isAssistantMessage(newMsg)) {
                                continue;
                            }

                            const nextText = toPlainText(getMessageContent(newMsg));
                            if (!nextText) {
                                continue;
                            }

                            const messageId = getMessageId(newMsg, fallbackAiMessageId);
                            assistantMessages.set(messageId, nextText);
                            const existingIndex = updatedMessages.findIndex((m) => m.id === messageId);

                            if (existingIndex === -1) {
                                updatedMessages.push({
                                    message: nextText,
                                    id: messageId,
                                    sender: "ai",
                                    direction: "incoming",
                                });
                            } else {
                                updatedMessages[existingIndex] = {
                                    ...updatedMessages[existingIndex],
                                    message: nextText,
                                };
                            }
                        }

                        return updatedMessages;
                    });
                }
            }
            await flushAssistantMessages();
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            void flushAssistantMessages();
            setDisabledSend(false);
            setTimeout(() => {
                if (input.current) {
                    input.current.focus();
                }
            }, 500)
        }
    };

    const handleSubmit = () => {
        if (!thread || disabledSend) {
            return;
        }
        handleSend(draft);
        setDraft("");
    };

    const handleInputKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSubmit();
        }
    };

    const getAvatarUrl = (message) => (
        message.sender === "human"
            ? "/avatars/user.svg"
            : "/avatars/assistant.svg"
    );

    const getNickname = (message) => (
        message.sender === "human" ? "You" : "Assistant"
    );

    return (
        <div className="App">
            <div className="--dark-theme" id="chat">
                <div className="chat__conversation-board" ref={boardRef}>
                    {messages.map((message) => {
                        const isOutgoing = message.direction === "outgoing" || message.sender === "human";
                        const containerClass = `chat__conversation-board__message-container${isOutgoing ? " reversed" : ""}`;
                        const messageText = typeof message.message === "string" ? message.message : "";
                        const bubbles = messageText.split(/\n+/).filter((line) => line.trim().length > 0);
                        const bubbleLines = bubbles.length > 0 ? bubbles : [messageText];

                        return (
                            <div className={containerClass} key={message.id}>
                                <div className="chat__conversation-board__message__person">
                                    <div className="chat__conversation-board__message__person__avatar">
                                        <img src={getAvatarUrl(message)} alt={getNickname(message)} />
                                    </div>
                                    <span className="chat__conversation-board__message__person__nickname">{getNickname(message)}</span>
                                </div>
                                <div className="chat__conversation-board__message__context">
                                    {bubbleLines.map((line, lineIndex) => (
                                        <div className="chat__conversation-board__message__bubble" key={`${message.id}-${lineIndex}`}>
                                            <span>{line}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="chat__conversation-panel">
                    <div className="chat__conversation-panel__container">
                        <input
                            className="chat__conversation-panel__input panel-item"
                            ref={input}
                            placeholder="Type a message..."
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            onKeyDown={handleInputKeyDown}
                            disabled={!thread || disabledSend}
                        />
                        <button
                            className={`chat__conversation-panel__button panel-item btn-icon send-message-button${disabledSend ? " is-loading" : ""}`}
                            type="button"
                            onClick={handleSubmit}
                            disabled={!thread || disabledSend}
                            aria-busy={disabledSend}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <line x1="22" y1="2" x2="11" y2="13"></line>
                                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default App;
